import { defineComponent, computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { collection, onSnapshot } from 'firebase/firestore';
import { Boxes, CircleDollarSign, ClipboardList, Package, TriangleAlert } from 'lucide-vue-next';
import { db } from '@/firebase';
export default defineComponent({
    name: 'AdminStatistics',
    components: { Boxes, CircleDollarSign, ClipboardList, Package, TriangleAlert },
    setup() {
        const inventory = ref([]);
        const orders = ref([]);
        const loading = ref(true);
        const inventoryError = ref('');
        const ordersError = ref('');
        const sectionsVisible = ref({ categories: false, lowStock: false, orders: false });
        const categoryPanel = ref(null);
        const lowStockPanel = ref(null);
        const ordersPanel = ref(null);
        let inventoryUnsubscribe = null;
        let ordersUnsubscribe = null;
        let sectionObserver = null;
        let ordersListenerStarted = false;
        const totalUnits = computed(() => inventory.value.reduce((sum, item) => sum + item.stock, 0));
        const stockValue = computed(() => inventory.value.reduce((sum, item) => sum + item.regularPrice * item.stock, 0));
        const lowStockItems = computed(() => inventory.value
            .filter(item => item.stock <= item.lowStockThreshold)
            .sort((left, right) => left.stock - right.stock));
        const categoryStats = computed(() => {
            const counts = new Map();
            inventory.value.forEach(item => {
                const category = item.category.trim() || 'Uncategorized';
                counts.set(category, (counts.get(category) ?? 0) + 1);
            });
            const maxCount = Math.max(...counts.values(), 1);
            return Array.from(counts, ([name, count]) => ({
                name,
                count,
                percent: Math.round((count / maxCount) * 100)
            })).sort((left, right) => right.count - left.count || left.name.localeCompare(right.name));
        });
        const orderStatusStats = computed(() => {
            const counts = new Map();
            orders.value.forEach(order => {
                const status = order.status.trim() || 'Unspecified';
                counts.set(status, (counts.get(status) ?? 0) + 1);
            });
            return Array.from(counts, ([name, count]) => ({ name, count }))
                .sort((left, right) => right.count - left.count || left.name.localeCompare(right.name));
        });
        const formatNumber = (value) => new Intl.NumberFormat('en-ZA').format(value);
        const formatMoney = (value) => new Intl.NumberFormat('en-ZA', {
            style: 'currency',
            currency: 'ZAR',
            maximumFractionDigits: 2
        }).format(value);
        const startOrdersListener = () => {
            if (ordersListenerStarted)
                return;
            ordersListenerStarted = true;
            ordersUnsubscribe = onSnapshot(collection(db, 'orders'), snapshot => {
                orders.value = snapshot.docs.map(document => ({
                    id: document.id,
                    status: String(document.data().status ?? '')
                }));
            }, error => {
                console.error('Order statistics subscription failed:', error);
                ordersError.value = 'Order statistics are unavailable. Inventory statistics are still shown.';
            });
        };
        const startInventoryListener = () => {
            inventoryUnsubscribe = onSnapshot(collection(db, 'inventory'), snapshot => {
                inventory.value = snapshot.docs.map(document => {
                    const data = document.data();
                    return {
                        id: document.id,
                        name: String(data.name ?? ''),
                        internalSku: String(data.internalSku ?? data.sku ?? ''),
                        category: String(data.category ?? ''),
                        regularPrice: Number(data.regularPrice ?? data.price ?? 0),
                        stock: Number(data.stock ?? 0),
                        lowStockThreshold: Number(data.lowStockThreshold ?? 0)
                    };
                });
                loading.value = false;
            }, error => {
                console.error('Inventory statistics subscription failed:', error);
                inventoryError.value = 'Could not load inventory statistics. Check Firestore permissions.';
                loading.value = false;
            });
        };
        const observeLazySections = async () => {
            await nextTick();
            if (typeof IntersectionObserver === 'undefined') {
                sectionsVisible.value = { categories: true, lowStock: true, orders: true };
                startOrdersListener();
                return;
            }
            sectionObserver?.disconnect();
            sectionObserver = new IntersectionObserver(entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting)
                        return;
                    const section = entry.target.dataset.lazySection;
                    if (section === 'categories' || section === 'lowStock' || section === 'orders') {
                        sectionsVisible.value[section] = true;
                        sectionObserver?.unobserve(entry.target);
                        if (section === 'orders')
                            startOrdersListener();
                    }
                });
            }, { rootMargin: '240px 0px' });
            [categoryPanel.value, lowStockPanel.value, ordersPanel.value].forEach(panel => {
                if (panel)
                    sectionObserver?.observe(panel);
            });
        };
        watch(loading, isLoading => {
            if (!isLoading)
                void observeLazySections();
        });
        onMounted(() => {
            startInventoryListener();
        });
        onUnmounted(() => {
            sectionObserver?.disconnect();
            inventoryUnsubscribe?.();
            ordersUnsubscribe?.();
        });
        return {
            inventory,
            orders,
            loading,
            inventoryError,
            ordersError,
            sectionsVisible,
            categoryPanel,
            lowStockPanel,
            ordersPanel,
            totalUnits,
            stockValue,
            lowStockItems,
            categoryStats,
            orderStatusStats,
            formatNumber,
            formatMoney
        };
    }
});
